import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useAppBranding } from '../../../config/branding';
import DefaultApiLimit from '../../../config/defaultLimit';
import { ByTransactionInterFace } from '../../../interface/by-transaction-interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableByTransactionFilterApplied,
  fetchByTransactionAccount,
  fetchByTransactionAccountFilters,
  updateByTransactionFilters,
  setSortData,
} from '../../../redux/slices/ByTransactionSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import CustomDateRangePicker from '../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import capitalizeFirstLetter from '../../../shared-functions/StringFunctions';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import accountApi from '../../../utils/api/account';

/**
 * By transaction data table component
 * @returns
 */
function ByTransactionAccount() {
  const branding = useAppBranding();
  const { t } = useTranslation('account');
  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();
  const accountCustomId = searchParameters.get('accountCustomId');
  const {
    ByTransaction,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filters,
    filterListLoading,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.byTransaction);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });

  const column = [
    {
      label: t('date'),
      accessorKey: 'transactionStartsAt' as keyof ByTransactionInterFace,
      sortAllow: true,
      /**
       * Converting timestamp to UTC date
       * @param byTransaction
       * @returns
       */
      customData: (byTransaction: ByTransactionInterFace) => (
        <p>{ConvertUTCtoDate(byTransaction.transactionStartsAt)}</p>
      ),
    },
    {
      label: t('userName'),
      accessorKey: 'userName' as keyof ByTransactionInterFace,
      sortAllow: true,
    },
    {
      label: t('email'),
      accessorKey: 'userEmail' as keyof ByTransactionInterFace,
    },
    {
      label: t('phoneNumber'),
      accessorKey: 'userPhoneNumber' as keyof ByTransactionInterFace,
    },
    {
      label: t('station'),
      accessorKey: 'chargingStationName' as keyof ByTransactionInterFace,
    },
    {
      label: t('device'),
      accessorKey: 'chargingPointSn' as keyof ByTransactionInterFace,
    },
    {
      label: t('amount'),
      accessorKey: 'amount' as keyof ByTransactionInterFace,
      sortAllow: true,
      headingDetails: true,
      headingDescription: (
        <>
          {t('totalAmountDescription')} {}
        </>
      ),
      /**
       * Rendering amount with two decimals
       * @param byTransaction
       * @returns
       */
      customData: (byTransaction: ByTransactionInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(byTransaction.amount)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('transactionId'),
      accessorKey: '_id' as keyof ByTransactionInterFace,
    },
    {
      label: t('kWhConsumed'),
      accessorKey: 'powerConsumed' as keyof ByTransactionInterFace,
      sortAllow: true,
      /**
       * KWh Consumed
       */
      customData: (currentRow: ByTransactionInterFace) => {
        const value = Number(currentRow?.powerConsumed) / 1000;
        return (
          <div className="value-container">
            <p className="value">{ToFixedValue(value)}</p>
            <span>kWh</span>
          </div>
        );
      },
    },
    {
      label: t('utilityCost'),
      accessorKey: 'utilityCost' as keyof ByTransactionInterFace,
      sortAllow: false,
      /**
       * KWh Consumed
       */
      customData: (currentRow: ByTransactionInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(currentRow.utilityCost)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('totalUtilityCost'),
      accessorKey: 'energyCost' as keyof ByTransactionInterFace,
      sortAllow: false,
      headingDetails: true,
      headingDescription: <>{t('totalUtilityCostDescription')}</>,
      /**
       * KWh Consumed
       */
      customData: (currentRow: ByTransactionInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(currentRow.energyCost)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('pricing'),
      accessorKey: 'pricing' as keyof ByTransactionInterFace,
      sortAllow: true,
      /**
       * Rendering pricing with two decimals
       * @param byTransaction
       * @returns
       */
      customData: (byTransaction: ByTransactionInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(byTransaction?.pricing?.price)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('totalCostPricing'),
      accessorKey: 'totalEnergyAmount' as keyof ByTransactionInterFace,
      sortAllow: false,
      headingDetails: true,
      headingDescription: <>{t('totalPowerCostDescription')}</>,
      /**
       * Rendering pricing with two decimals
       * @param byTransaction
       * @returns
       */
      customData: (byTransaction: ByTransactionInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(byTransaction?.totalEnergyAmount)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('adminShare'),
      accessorKey: 'adminRevenue' as keyof ByTransactionInterFace,
      sortAllow: true,
      headingDetails: true,
      headingDescription: <>{t('adminSharePercentDescription')}</>,
      /**
       * Rendering admin share with two decimals
       * @param byTransaction
       * @returns
       */
      customData: (byTransaction: ByTransactionInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(byTransaction.adminRevenue)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('adminSharePercent'),
      accessorKey: 'adminRevenuePercentage' as keyof ByTransactionInterFace,
      sortAllow: true,
      /**
       * Rendering admin share with two decimals
       * @param byTransaction
       * @returns
       */
      customData: (byTransaction: ByTransactionInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(byTransaction.adminRevenuePercentage)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('facilityShare'),
      accessorKey: 'foRevenue' as keyof ByTransactionInterFace,
      sortAllow: true,
      headingDetails: true,
      headingDescription: <>{t('facilitySharePercentDescription')}</>,
      /**
       * Rendering admin share with two decimal
       * @param byTransaction
       * @returns
       */
      customData: (byTransaction: ByTransactionInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(byTransaction.foRevenue)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('facilitySharePercent'),
      accessorKey: 'foRevenuePercentage' as keyof ByTransactionInterFace,
      sortAllow: true,
      /**
       * Rendering admin share with two decimals
       * @param byTransaction
       * @returns
       */
      customData: (byTransaction: ByTransactionInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(byTransaction.foRevenuePercentage)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('idleFee'),
      accessorKey: 'penaltyAmount' as keyof ByTransactionInterFace,
      sortAllow: true,
      /**
       * Rendering admin share with two decimals
       * @param byTransaction
       * @returns
       */
      customData: (byTransaction: ByTransactionInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(byTransaction.penaltyAmount)}</p>
        </div>
      ),
    },
    {
      label: t('platformFee'),
      accessorKey: 'platformFee' as keyof ByTransactionInterFace,
      sortAllow: false,
      /**
       * Rendering platform fee with two decimals
       * @param byTransaction
       * @returns
       */
      customData: (byTransaction: ByTransactionInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(byTransaction.platformAmount)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('sessionFee'),
      accessorKey: 'sessionAmount' as keyof ByTransactionInterFace,
      sortAllow: true,
      /**
       * Rendering session fee with two decimals
       * @param byTransaction
       * @returns
       */
      customData: (byTransaction: ByTransactionInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(byTransaction.sessionAmount)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('paymentMethod'),
      accessorKey: 'paymentMethod' as keyof ByTransactionInterFace,
    },
    {
      label: t('paymentStatus'),
      accessorKey: 'paymentStatus' as keyof ByTransactionInterFace,
      /**
       * Payment status view in table
       * @param payouts
       * @returns
       */
      customData: (payoutsStatus: ByTransactionInterFace) => {
        const statusText = payoutsStatus.paymentStatus
          ? capitalizeFirstLetter(payoutsStatus.paymentStatus)
          : '';

        return (
          <div className="status-in-payout-table">
            <p
              className={`${
                payoutsStatus.paymentStatus === 'success' ||
                payoutsStatus.paymentStatus === 'processed'
                  ? 'success-text'
                  : 'failed'
              }`}>
              {statusText}
            </p>
          </div>
        );
      },
    },
    {
      label: t('guestUserTransaction'),
      accessorKey: 'isFreeTransaction' as keyof ByTransactionInterFace,
      /**
       * Payment status view in table
       * @param payouts
       * @returns
       */
      customData: (payoutsStatus: ByTransactionInterFace) =>
        payoutsStatus.isFreeTransaction ? <>{t('yes')}</> : <>{t('no')}</>,
    },
  ];

  /**
   * Clears the date range values
   */
  const clearDateRange = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Fetch Users list function
   */
  const fetchData = (data: any) => {
    if (!loading && accountCustomId) {
      dispatch(fetchByTransactionAccount({ accountCustomId, ...data }));
    }
  };

  /**
   * Table pagination function
   * @param value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...value,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      ...queryParameters,
      sortBy,
      sortField,
    });
  };

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    if (!accountCustomId) return;
    const query = {
      ...queryParameters,
      accountCustomId,
    };
    if (sortBy && sortField) {
      query.sortBy = sortBy;
      query.sortField = sortField;
    }
    setExportLoader(true);
    accountApi
      .fetchByTransactionExportData(query)
      .then((response) => {
        const visibleColumns = column.filter(
          (col) => !hiddenColumns.includes(col.accessorKey as string),
        );

        const headers = [t('sNo'), ...visibleColumns.map((col) => col.label)];

        const currencyColumns = [
          'amount',
          'utilityCost',
          'energyCost',
          'pricing',
          'totalEnergyAmount',
          'adminRevenue',
          'adminRevenuePercentage',
          'foRevenue',
          'foRevenuePercentage',
          'platformFee',
          'sessionAmount',
        ];

        const rows = response.data.map((row: ByTransactionInterFace, index: number) => [
          index + 1,
          ...visibleColumns.map((col) => {
            let value = row[col.accessorKey];
            if (col?.accessorKey === 'userEmail' && !value) {
              value = (row as any)?.email;
            }
            if (value === undefined || value === null || value === '') {
              return t('notAvailable');
            }
            if (currencyColumns.includes(col.accessorKey as string)) {
              return `${ToFixedValue(Number(value))} ${branding.text.currency}`;
            }
            return value;
          }),
        ]);

        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'reports-list.csv');
          } else {
            exportPDF(headers, rows, 'reports-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };
  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      page,
      limit,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      ...queryParameters,
      sortBy,
      sortField,
    });
  };

  /**
   * Render the date filters
   * @returns
   */
  const renderDateFilters = () => {
    return (
      <div className="date-content">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
        />
      </div>
    );
  };

  useEffect(() => {
    if (isFilterUpdated || ByTransaction.length === 0 || dateRange.startDate || dateRange.endDate) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        accountCustomId,
        page: DefaultApiLimit.page,
        limit,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        ...queryParameters,
        sortBy,
        sortField,
      });
      dispatch(disableByTransactionFilterApplied());
    }
  }, [filters, dateRange.endDate, dateRange.startDate]);

  return (
    <div>
      <CustomTable
        version="v2"
        header={column}
        data={ByTransaction}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        serialNo
        sortData={{ sortBy, sortField }}
        pageLimitChange={pageNateLimitChange}
        handleSortChange={handleSortFunction}
        onHiddenColumnsChange={setHiddenColumns}
        filters={filters}
        filtersToDispatch={updateByTransactionFilters}
        filterListLoading={filterListLoading}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        filterListToDispatch={fetchByTransactionAccountFilters}
        exportLoader={exportLoader}
        exportData={ByTransaction}
        ReferenceCurrentPages={handleRefresh}
        refreshRequired
        hoverLabel={t('refresh')}
        sizeChangeButtonRequired
        height={window.innerHeight >= 700 ? 347 : 349}
        rightTopContent={renderDateFilters()}
        onClearFilterChange={() => {
          clearDateRange();
        }}
      />
    </div>
  );
}

export default ByTransactionAccount;
